import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {formatProfileQuickInfo} from "crqlar-react-common/dist/utils/profile.util";
import {Sector, SectorType } from "crqlar-react-common/dist/models/sector/sector.model";
import { UnassignedType } from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {
    findPrimaryResourceIdForRes,
    hasRequiredResources
} from "crqlar-react-common/dist/utils/hooks/reservations/reservations.utils";
import {
    MbscCalendarEvent
} from "@mobiscroll/react";
import {diffInMinutes} from "crqlar-react-common/dist/utils/date.util";
import { AvailabilityType } from "crqlar-react-common/dist/models/availability/availability.model";
import { Profile } from "crqlar-react-common/dist/models/profile/profile.model";
import { Service } from "crqlar-react-common/dist/models/service/service.model";
import { Resource } from "crqlar-react-common/dist/models/resource/resource.model";
import {UseQueryResult} from "react-query";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";

export interface EventGenerationConfig {
    sector?: Sector
    profiles?: Profile[]
    services?: Service[]
    resourcesFilteredByCategory: Resource[],
    allResources: Resource[]
    allChildResources: Resource[],
    selectedResources?: Resource[],
    sectorServices?: Service[],
    availabilitiesCalculationResults?: AvailabilityCalculationResult[]
    mbscResourceColumnType: "services" | "resources" | undefined,
    usesChildrenResources: boolean,
    restaurantSectorId?: string
}

export const generateReservationEvents = (
    reservationsList: Reservation[],
    config: EventGenerationConfig
) => {
    const {
        sector,
        profiles,
        services,
        resourcesFilteredByCategory,
        allResources,
        allChildResources,
        selectedResources,
        sectorServices,
        availabilitiesCalculationResults,
        mbscResourceColumnType,
        usesChildrenResources,
        restaurantSectorId
    } = config

    let calEvents: MbscCalendarEvent[] = []
    reservationsList.forEach(reservation => {
        const reservationProfiles = profiles?.filter(p => reservation?.profileIds?.includes(p.id!)) ?? []
        // Primary profile first (same pick as the calendar tooltip); profiles arrive sorted by name
        const profileForRes = [
            ...reservationProfiles.filter(p => p.id === reservation.primaryProfileId),
            ...reservationProfiles.filter(p => p.id !== reservation.primaryProfileId)
        ]

        // Now the color is based on the service color and not on reservation status
        const eventColor = services?.find(s => s.id === reservation.serviceId)?.color?.background

        // Parent resource is the resource find in resources list
        let parentResourceId = findPrimaryResourceIdForRes(reservation, resourcesFilteredByCategory as Resource[])

        /*
            Here we assign some virtual resource ids for mobiscroll bind & show purposes
            We create clones instead of saving the new reservation state with these dummy resource ids
        */
        let reservationResourceIds: string[] | undefined

        // Left mobiscroll resource column can present resources (as it was until now) and reservation services
        switch (mbscResourceColumnType) {
            case 'services':
                reservationResourceIds = reservation.serviceId ? [reservation.serviceId]
                    : [`${UnassignedType.UNASSIGNED_SERVICE}-${sector?.id}`]
                break
            case 'resources':
            default:
                reservationResourceIds = reservation.resourceIds
                // We need to combine resources and childrenResources in case of restaurant sector
                const reservationResources = [...allResources, ...allChildResources].filter(r => reservation.resourceIds?.includes(r.id!))

                const service = sectorServices?.find(s => s.id === reservation.serviceId)

                // In order for slot to be shown in 'unassigned' row - at least one of the reservation resources needs to be in the selected resources filter
                const anyResourceInFilter = reservationResources.some(r => selectedResources?.some(sr => sr.id === r.id))

                // Reservations which do not meet condition of num of required resources are also placed in 'unassigned' row
                if (
                    (
                        // Either no resources
                        !reservationResourceIds?.length ||
                        // Or resource requirements not met
                        !hasRequiredResources(service, reservationResources, restaurantSectorId)
                    ) &&
                    (
                        // Either no resources
                        !reservationResourceIds?.length ||
                        // Or at least one of the resources is in filter (RHS)
                        anyResourceInFilter
                    )
                ) {
                    reservationResourceIds = [`${UnassignedType.UNASSIGNED_RESOURCE}-${sector?.id}`];
                }
                break
        }

        reservationResourceIds?.forEach(resourceId => {
            // skip resource ids which are restaurant ids
            if (usesChildrenResources && resourcesFilteredByCategory.some(r => r.id === resourceId)) return

            let title = formatProfileQuickInfo(profileForRes?.[0], {
                withName: true,
                withRoom: false,
                profileNameReversed: true
            })
            if (sector?.sectorType == SectorType.ACCOMMODATION) {
                title = formatProfileQuickInfo(profileForRes?.[0], {
                    withName: true,
                    withRate: true,
                    withVIP: true
                })
            }
            if (!title) {
                title = [reservation.reservationRequestData?.firstName, reservation.reservationRequestData?.lastName].filter(Boolean).join(' ')
            }

            if (sector?.sectorType != SectorType.RESTAURANT) {
                parentResourceId = resourceId
            }

            const isMatchingResource = (av: { connectedResourceId?: string }) =>
                av.connectedResourceId === resourceId || av.connectedResourceId === parentResourceId

            const bufferAvailability = availabilitiesCalculationResults
                ?.find(av =>
                    av.connectedReservationId === reservation.id
                    && isMatchingResource(av)
                    && av.type === AvailabilityType.CLOSED
                    && !!av.bufferTimeInfo
                )

            const preparationAvailability = availabilitiesCalculationResults
                ?.find(av =>
                    av.connectedReservationId === reservation.id
                    && isMatchingResource(av)
                    && av.type === AvailabilityType.CLOSED
                    && !!av.preparationTimeInfo
                )


            // Unsaved reservations (the in-progress package preview) get a stable id so Mobiscroll
            // doesn't mint a new one each render, and cannot be dragged — there is nothing to update yet.
            const reservationIndex = reservationsList.indexOf(reservation)
            calEvents.push({
                start: reservation.expectedStartDate,
                end: reservation.expectedEndDate,
                title: title,
                resource: resourceId,
                id: reservation.id ? `${reservation.id}-${resourceId}` : `tmp-${reservationIndex}-${resourceId}`,
                ...(reservation.id ? {} : {editable: false}),
                reservationId: reservation.id,
                color: eventColor,
                status: reservation.status,
                parentResourceId: parentResourceId,
                // Keeping the search results on the front
                order: reservationIndex,
                // Include buffer time only for the according resources
                bufferAfter: bufferAvailability ? diffInMinutes(bufferAvailability.endTime, bufferAvailability.startTime) : undefined,
                bufferBefore: preparationAvailability ? diffInMinutes(preparationAvailability.endTime, preparationAvailability.startTime) : undefined
            })
        })
    })

    return calEvents
}
